import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2" }
const _hoisted_4 = { class: "me-sm-3 flex-grow-1" }
const _hoisted_5 = { class: "mb-0" }
const _hoisted_6 = { class: "mb-0" }
const _hoisted_7 = { class: "mb-2 ms-sm-auto" }
const _hoisted_8 = {
  key: 0,
  class: "mb-2"
}
const _hoisted_9 = {
  key: 0,
  hidden: ""
}
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = ["innerHTML"]
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { key: 4 }

import { computed, reactive, ref, PropType, watch } from 'vue';
import { useRouter } from 'vue-router';
import { openRouterLink } from '@/utils/router-utils';
import { formatDateTime, deepParseDates } from '@/utils/date-utils';
import {
  ComputeTaskUtils,
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import { highlightJson } from '@/utils/prism-utils';
import ComputeTaskDebugDetails from '@/components/compute/ComputeTaskDebugDetails.vue';
import TaskResultState from '@/components/compute/TaskResultState.vue';
import { useProjectTaskQuery } from '@/composables/project-task-polling';
import { BucketType, ComputeTaskType, DataObjectType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BModal,
  BPagination,
  BSpinner,
} from 'bootstrap-vue-next';
import {
  COMPUTE_TASK_STATUS,
  COMPUTE_TASK_TYPES,
  IS_DEBUG_MODE,
  PAGING_ITEMS_PER_PAGE,
} from '@/constants';
import ComputeTaskDetail, {
  TaskFileType,
} from '@/components/compute/ComputeTaskDetail.vue';
import ComputeTaskAbort from '@/components/compute/ComputeTaskAbort.vue';
import { useUserStore } from '@/store/user';
import { getFileDownloadUrl } from '@/utils/fileService';
import DataObjectDetail from '@/components/data/DataObjectDetail.vue';
import ComputeTaskFilesList from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskStatusBadge from '@/components/compute/ComputeTaskStatusBadge.vue';
import { getRootTasks, pageRootTasks } from '@/utils/compute-task-root-paging';

const colMinWidth = 450;

// --- Component State ---

export default /*@__PURE__*/_defineComponent({
  __name: 'ComputeTasksNested',
  props: {
  projectId: { type: Number as PropType<number | null>, default: null },
  taskTypes: { type: Array as PropType<string[] | null>, default: null },
  // Exclude parents by child task type.
  excludeSubtaskTypes: {
    type: Array as PropType<string[] | null>,
    default: null,
  },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  // additinal component props
  messageNoItemsFound: {
    type: String,
    default: 'No compute tasks found.',
  },
  taskNameColumnTitle: {
    type: String,
    default: 'Task Name',
  },
  showExecDetails: {
    type: Boolean,
    default: true,
  },
},
  setup(__props, { expose: __expose }) {

const router = useRouter();
const userStore = useUserStore();

// --- Component Properties ---
const props = __props;

// --- Set minimum card width ---
const disableTransitionConfig = ref<'none' | 'multi' | 'all'>('multi'); // none, multi (multiple cards only), all (disable single card animations too)
const disableTransitionsState = ref(disableTransitionConfig.value === 'all');
const selectedTask = ref<ComputeTaskType | null>(null);
const selectedDataObject = ref<DataObjectType | null>(null);
const showModalDataObjectDetail = ref(false);
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return tasksQueryResult.value?.computeTasks?.total || 0;
});
const pagerTotalRows = computed(() => rootTasks.value.length);

// Tab selection for tasks & subtasks
// get helper with safe default
const selectedTabByTaskId = reactive<
  Record<number, DataPipelineTaskTabOptionType>
>({});

function getSelectedTab(id?: number | null) {
  if (!id) {
    return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
  }
  const selectedTab = selectedTabByTaskId[id];
  if (selectedTab) {
    return selectedTab;
  }
  // otherwise check whether the task is root or subtask and set default tab accordingly
  const task = tasks.value.find((t) => t.id === id);
  if (task && task.parentId) {
    // subtask - default to progress
    return DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS;
  }

  // otherwise consider root task - default to files
  return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
}

function setSelectedTab(
  id?: number | null,
  val: DataPipelineTaskTabOptionType = DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
) {
  if (id) {
    selectedTabByTaskId[id] = val;
  }
}

// set selected tab to task and all subtasks
function setSelectedTabToAll(
  rootTask: ComputeTaskType,
  selectedTab: DataPipelineTaskTabOptionType
) {
  setSelectedTab(rootTask.id, selectedTab);
  rootTask.subtasks?.forEach((st) => {
    if (st?.id != null) {
      setSelectedTab(st.id, selectedTab);
    }
  });
}

// Expanded/Collapsed tasks & subtasks
// get helper with safe default
const expandedByTaskId = reactive<Record<number, boolean>>({});

function getExpanded(id?: number | null) {
  if (!id) {
    return false;
  }
  // if id not in the map, default to false unless root task with no parent and multiple subtasks
  if (expandedByTaskId[id] === undefined) {
    const task = rootTasks.value.find(
      (t) => t.id === id && t.taskType === COMPUTE_TASK_TYPES.COMPUTE_PIPELINE
    );
    if (task) {
      // root compute pipeline task - default to expanded view if files are available
      return !!(task.results.filesAvailable || task.results.files?.length);
    } else {
      // otherwise default to collapsed
      return false;
    }
  }
  // otherwise return the value
  return expandedByTaskId[id];
}

// write helper used by child event
function setExpanded(id?: number | null, val = false) {
  if (id) {
    expandedByTaskId[id] = val;
    return setTaskExpanded(id, val);
  }
}

// expand/collapse all subtasks for a given root task
function setExpandToAll(rootTask: ComputeTaskType, expand: boolean) {
  // disable transitions temporarily to avoid janky animations when expanding/collapsing multiple cards
  disableTransitionsState.value = disableTransitionConfig.value !== 'none';
  // expand/collapse root task and all subtasks
  setExpanded(rootTask.id, expand);
  rootTask.subtasks?.forEach((st) => {
    if (st?.id != null) {
      setExpanded(st.id, expand);
    }
  });
  // reset transitions state to default after a short delay to allow for layout changes
  setTimeout(() => {
    disableTransitionsState.value = disableTransitionConfig.value === 'all';
  }, 250);
}

// --- Compute Tasks List ---
const tasks = computed<ComputeTaskType[]>(() => {
  return (
    tasksQueryResult.value?.computeTasks?.computeTasks
      ?.filter((task) => task && task !== null)
      .map((task) => ({
        ...(deepParseDates(task) as ComputeTaskType),
      })) || []
  );
});

// --- Compute Task Hierarchy ---
const rootTasks = computed(() => getRootTasks(tasks.value));
const visibleRootTasks = computed(() =>
  pageRootTasks(
    rootTasks.value,
    pagingPageCurrent.value,
    props.pagingItemsPerPage
  )
);

// --- Component state ---

const noTasks = computed(() => {
  return tasks.value.length === 0;
});

// --- Query Compute Tasks ---
const {
  result: tasksQueryResult,
  loading: tasksQueryInProgress,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  detailErrors,
  setVisibleTasks,
  setExpanded: setTaskExpanded,
} = useProjectTaskQuery(
  computed(() => ({
    projectId: props.projectId,
    taskTypes: props.taskTypes,
    excludeSubtaskTypes: props.excludeSubtaskTypes,
    // date & time filters
    // paging specification: all rows, so a parent and its children stay one
    // card; visibleRootTasks pages the cards
    pagingItemsPerPage: 0,
  }))
);

watch(
  visibleRootTasks,
  (rows) => {
    setVisibleTasks(
      rows.flatMap((task) => [
        task.id!,
        ...(task.subtasks || []).map((child) => child!.id!),
      ])
    );
    for (const task of rows)
      if (getExpanded(task.id)) void setTaskExpanded(task.id!, true);
  },
  { immediate: true }
);

watch(pagerTotalRows, (total) => {
  if (props.pagingItemsPerPage > 0) {
    const lastPage = Math.max(1, Math.ceil(total / props.pagingItemsPerPage));
    if (pagingPageCurrent.value > lastPage) pagingPageCurrent.value = lastPage;
  }
});

onTasksQueryDone((res) => {
  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Tasks retrieved successfuly (ComputeTasksNested)');
    } else {
      console.error('Error, could not retrieve tasks:', res.data);
    }
  } else if (!res.loading) {
    console.error('Error, could not retrieve tasks result data:', res);
  }
});

onTasksQueryError((err) => {
  console.error('Error fetching code (onCodeError):', err.message);
});

function requestRefetchTasks() {
  refetchTasks()
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

function requestSearch() {
  pagingPageCurrent.value = 1;
  requestRefetchTasks();
}

// --- File Requests ---
function filePreviewRequested(task: ComputeTaskType, file: TaskFileType) {
  console.debug('file-preview-request', file);
  selectedTask.value = task;
  selectedDataObject.value = {
    isFolder: false,
    name: file.path,
  } as DataObjectType;
  showModalDataObjectDetail.value = true;
}

async function fileDownloadRequested(
  task: ComputeTaskType,
  file: TaskFileType
) {
  console.debug('file-download-request', file);
  // validate the selected data object
  if (!file.name) {
    console.error('No name found in selected data object');
    return;
  }
  if (!task?.bucket?.id) {
    console.error('Task Bucket ID not found');
    return;
  }
  // get the download URL
  const url = await getFileDownloadUrl(task.bucket.id, file.path);
  // validate the URL
  if (!url) {
    console.error(
      `Unexpected error fetching download URL for the file ${file.path}`
    );
    alert(
      'Failed to generate download link. The requested file might not be available anymore.'
    );
    return;
  }
  // open the URL in a new tab to trigger the download
  window.open(url, '_blank');
}

// --- Compute Task Abort Request ---
const showConfirmAbortModal = ref<boolean>(false);

function taskAbortRequested(task: ComputeTaskType) {
  if (!task?.id) {
    console.error('Task cancellation requested, but task ID is missing');
    return;
  }
  console.debug('Cancel task ', task.id, 'requested, waiting for confirmation');
  selectedTask.value = task;
  showConfirmAbortModal.value = true;
}

function closeTaskAbortModal() {
  showConfirmAbortModal.value = false;
}

// --- Debug Details ---
const debugTaskId = ref<number | null>(null);
const detailsContent = ref<any>(null);
const detailsModalTitle = ref('Compute Task Details');
const showDetailsModal = ref(false);

async function showExecDetails(task: ComputeTaskType) {
  if (task.id) {
    const loaded = await setExpanded(task.id, true);
    if (!loaded) return;
    task = loaded;
  }
  detailsModalTitle.value = task.name || 'Compute Task Exec Details';
  let censoredAttributes = task.attributes;

  // only allow certain attributes to be copied and viewed if not admin/developer
  if (!userStore.admin || !userStore.developer) {
    censoredAttributes = Object.fromEntries(
      Object.entries(task.attributes).filter(([key]) =>
        [
          // common attribues
          'samples', // bcl to fastq conversion + data pipeline
          'version',
          // ftp transfer
          'ftpCmd',
          'autoAnnotate',
          'reconstructData',
          'createProject',
          // data pipeline
          'startPhase',
          'endPhase',
          'bucketOutput',
        ].includes(key)
      )
    );
    // if task.attributes.samples is available, convert the array of objects to array of strings by taking the object's name property only
    if (Array.isArray(censoredAttributes.samples)) {
      censoredAttributes.samples = censoredAttributes.samples.map(
        (sample: { name: string }) => sample.name
      );
    }
  }
  detailsContent.value = censoredAttributes;
  showDetailsModal.value = true;
}

// --- Expose Properties to the Outside ---
__expose({
  tasks,
  pagingTotalRows,
  tasksQueryInProgress,
  requestSearch,
});

// --- Manage Cards Layout Changes ---
function onLayoutNeeded(phase: 'start' | 'end' | 'resize', id?: number | null) {
  console.debug('Layout requested, phase: ', phase, 'id:', id);

  // check task ID provided
  if (!id) {
    console.warn('Layout requested but no task ID provided');
    return;
  }

  // identify the container element for the task
  const container = document.querySelector(`#task-${id}`) as HTMLElement | null;

  // check container exists
  if (!container) {
    console.warn(`Layout requested but container #task-${id} not found`);
    return;
  }

  // pause transitions while heights are changing
  if (phase === 'start' && disableTransitionsState.value) {
    console.debug('Pausing transitions for layout');
    container.classList.add('task-no-anim');
  }

  // resume transitions when layout is complete
  if (phase === 'end' && disableTransitionsState.value) {
    requestAnimationFrame(() => {
      console.debug('Resuming transitions after layout');
      container.classList.remove('task-no-anim');
    });
  }
}

// --- Router Helpers for Navigation ---
function bucketRequested(
  bucket: BucketType | undefined | null,
  index?: number,
  evt?: MouseEvent
) {
  if (bucket && bucket.id) {
    // redirect to bucket detail page if permitted
    if (!bucket.deletedAt) {
      openRouterLink(
        {
          name: 'BucketDetail',
          params: { id: bucket.id },
          query: {
            showMetadata: '1',
            allowDownload: '1',
            listComputeTasks: '0',
            listSamples: '0',
            listProjects: '0',
            listUsers: '0',
            returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
            returnLabel: 'Project Detail',
          },
        },
        router,
        evt
      );
    } else {
      alert('The bucket for this task has been deleted.');
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _directive_b_tooltip = _resolveDirective("b-tooltip")!

  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(tasksQueryInProgress))
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 0,
          variant: "info"
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode("Loading...", -1)
          ]))]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_unref(tasksQueryError))
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 1,
          variant: "danger"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(tasksQueryError).message) + " ", 1),
            _createVNode(_unref(BButton), {
              onClick: requestRefetchTasks,
              variant: "outline-dark",
              class: "btn-sm"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, { icon: "sync" }),
                _cache[7] || (_cache[7] = _createTextVNode(" Retry ", -1))
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      "model-value": noTasks.value,
      variant: "secondary"
    }, {
      default: _withCtx(() => [
        (_unref(tasksQueryInProgress))
          ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
              _cache[8] || (_cache[8] = _createTextVNode(" Loading tasks ", -1)),
              _createVNode(_unref(BSpinner), { small: "" })
            ]))
          : (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(props.messageNoItemsFound), 1))
      ]),
      _: 1
    }, 8, ["model-value"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleRootTasks.value, (rootTask, idx) => {
      return (_openBlock(), _createElementBlock(_Fragment, {
        key: rootTask.id || idx
      }, [
        (getExpanded(rootTask.id))
          ? (_openBlock(), _createBlock(TaskResultState, {
              key: 0,
              error: _unref(detailErrors)[rootTask.id!]
            }, null, 8, ["error"]))
          : _createCommentVNode("", true),
        (rootTask.subtasks?.length === 0)
          ? (_openBlock(), _createBlock(ComputeTaskDetail, {
              key: 1,
              id: `task-${rootTask.id}`,
              task: rootTask,
              class: "mb-2",
              expanded: getExpanded(rootTask.id),
              "selected-tab": getSelectedTab(rootTask.id),
              "onUpdate:expanded": (val) => setExpanded(rootTask.id ?? 0, val),
              "onUpdate:selectedTab": val => setSelectedTab(rootTask.id!, val),
              onLayoutNeeded: onLayoutNeeded,
              onBucketRequested: ($event: any) => (bucketRequested(rootTask.bucket, idx, $event)),
              onExecDetailsRequested: ($event: any) => (showExecDetails(rootTask)),
              onDebugDetailsRequested: ($event: any) => (debugTaskId.value = rootTask.id || null),
              onAbortRequested: ($event: any) => (taskAbortRequested(rootTask)),
              onFilePreviewRequested: 
            (file) => filePreviewRequested(rootTask, file)
          ,
              onFileDownloadRequested: 
            (file) => fileDownloadRequested(rootTask, file)
          
            }, null, 8, ["id", "task", "expanded", "selected-tab", "onUpdate:expanded", "onUpdate:selectedTab", "onBucketRequested", "onExecDetailsRequested", "onDebugDetailsRequested", "onAbortRequested", "onFilePreviewRequested", "onFileDownloadRequested"]))
          : (_openBlock(), _createBlock(_unref(BCard), {
              key: 2,
              class: "mb-2 root-task-card"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("h2", _hoisted_5, _toDisplayString(rootTask.name) + " (legacy)", 1),
                    _createElementVNode("p", _hoisted_6, [
                      _createElementVNode("small", null, _toDisplayString(rootTask.subtasks?.length) + " subtasks, created " + _toDisplayString(_unref(formatDateTime)(rootTask.createdAt)), 1)
                    ]),
                    _createVNode(ComputeTaskStatusBadge, { task: rootTask }, null, 8, ["task"])
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(BButtonGroup), { class: "me-2 mb-1" }, {
                      default: _withCtx(() => [
                        _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                          size: "sm",
                          variant: "outline-secondary",
                          title: "Expand all",
                          onClick: ($event: any) => (setExpandToAll(rootTask, true)),
                          id: `btn-expand-${rootTask.id}`
                        }, {
                          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                            _createElementVNode("i", { class: "bi bi-arrows-expand" }, null, -1)
                          ]))]),
                          _: 1
                        }, 8, ["onClick", "id"])), [
                          [
                            _directive_b_tooltip,
                            void 0,
                            void 0,
                            { hover: true }
                          ]
                        ]),
                        _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                          size: "sm",
                          variant: "outline-secondary",
                          onClick: ($event: any) => (setExpandToAll(rootTask, false)),
                          id: `btn-collapse-${rootTask.id}`,
                          title: "Collapse all"
                        }, {
                          default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                            _createElementVNode("i", { class: "bi bi-arrows-collapse" }, null, -1)
                          ]))]),
                          _: 1
                        }, 8, ["onClick", "id"])), [
                          [
                            _directive_b_tooltip,
                            void 0,
                            void 0,
                            { hover: true }
                          ]
                        ])
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(_unref(BButtonGroup), { class: "mb-1" }, {
                      default: _withCtx(() => [
                        (rootTask.bucketId || rootTask.bucket)
                          ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                              key: 0,
                              size: "sm",
                              variant: "outline-dark",
                              onClick: ($event: any) => {
                  setExpanded(rootTask.id, true);
                  setSelectedTabToAll(
                    rootTask,
                    _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS
                  );
                },
                              id: `btn-progress-${rootTask.id}`,
                              title: "View progress timeline with stats",
                              active: 
                  getExpanded(rootTask.id) &&
                  getSelectedTab(rootTask.id) ===
                    _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS
                
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_font_awesome_icon, { icon: "list-check" })
                              ]),
                              _: 1
                            }, 8, ["onClick", "id", "active"])), [
                              [
                                _directive_b_tooltip,
                                void 0,
                                void 0,
                                { hover: true }
                              ]
                            ])
                          : _createCommentVNode("", true),
                        (rootTask.bucketId || rootTask.bucket)
                          ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                              key: 1,
                              hidden: "",
                              size: "sm",
                              variant: "outline-dark",
                              onClick: ($event: any) => {
                  setExpanded(rootTask.id, true);
                  setSelectedTabToAll(
                    rootTask,
                    _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FIGURES
                  );
                },
                              id: `btn-figures-${rootTask.id}`,
                              title: "View figures",
                              active: 
                  getExpanded(rootTask.id) &&
                  getSelectedTab(rootTask.id) ===
                    _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FIGURES
                
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_font_awesome_icon, { icon: "chart-line" })
                              ]),
                              _: 1
                            }, 8, ["onClick", "id", "active"])), [
                              [
                                _directive_b_tooltip,
                                void 0,
                                void 0,
                                { hover: true }
                              ]
                            ])
                          : _createCommentVNode("", true),
                        (rootTask.bucketId || rootTask.bucket)
                          ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                              key: 2,
                              size: "sm",
                              variant: "outline-dark",
                              onClick: ($event: any) => {
                  setExpanded(rootTask.id, true);
                  setSelectedTabToAll(
                    rootTask,
                    _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES
                  );
                },
                              id: `btn-files-${rootTask.id}`,
                              title: "View files",
                              active: 
                  getExpanded(rootTask.id) &&
                  getSelectedTab(rootTask.id) ===
                    _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES
                
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_font_awesome_icon, { icon: "file" })
                              ]),
                              _: 1
                            }, 8, ["onClick", "id", "active"])), [
                              [
                                _directive_b_tooltip,
                                void 0,
                                void 0,
                                { hover: true }
                              ]
                            ])
                          : _createCommentVNode("", true),
                        _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                          variant: "outline-dark",
                          size: "sm",
                          onClick: ($event: any) => (showExecDetails(rootTask)),
                          id: `btn-exec-${rootTask.id}`,
                          title: "View exec configuration"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'sliders'] })
                          ]),
                          _: 1
                        }, 8, ["onClick", "id"])), [
                          [
                            _directive_b_tooltip,
                            void 0,
                            void 0,
                            { hover: true }
                          ]
                        ]),
                        (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                          ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                              key: 3,
                              variant: "outline-dark",
                              size: "sm",
                              onClick: ($event: any) => (bucketRequested(rootTask.bucket, idx, $event)),
                              id: `btn-bucket-${rootTask.id}`,
                              disabled: !rootTask.bucket,
                              title: "Go to data"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_font_awesome_icon, { icon: ['fas', 'database'] })
                              ]),
                              _: 1
                            }, 8, ["onClick", "id", "disabled"])), [
                              [
                                _directive_b_tooltip,
                                void 0,
                                void 0,
                                { hover: true }
                              ]
                            ])
                          : _createCommentVNode("", true),
                        (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                          ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                              key: 4,
                              variant: "outline-dark",
                              size: "sm",
                              onClick: ($event: any) => (debugTaskId.value = rootTask.id || null),
                              id: `btn-debug-${rootTask.id}`,
                              title: "View debug details"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_font_awesome_icon, { icon: ['fas', 'code'] })
                              ]),
                              _: 1
                            }, 8, ["onClick", "id"])), [
                              [
                                _directive_b_tooltip,
                                void 0,
                                void 0,
                                { hover: true }
                              ]
                            ])
                          : _createCommentVNode("", true),
                        _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                          size: "sm",
                          variant: "outline-danger",
                          disabled: !_unref(ComputeTaskUtils).canBeAborted(rootTask.status),
                          onClick: ($event: any) => (taskAbortRequested(rootTask)),
                          id: `btn-abort-${rootTask.id}`,
                          title: "Abort task",
                          active: rootTask.status === _unref(COMPUTE_TASK_STATUS).ABORTING
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "times" })
                          ]),
                          _: 1
                        }, 8, ["disabled", "onClick", "id", "active"])), [
                          [
                            _directive_b_tooltip,
                            void 0,
                            void 0,
                            { hover: true }
                          ]
                        ])
                      ]),
                      _: 2
                    }, 1024)
                  ])
                ]),
                (getExpanded(rootTask.id))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      (
              getSelectedTab(rootTask.id) ===
              _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS
            )
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [...(_cache[11] || (_cache[11] = [
                            _createElementVNode("hr", null, null, -1),
                            _createElementVNode("small", null, "Not yet implemented", -1)
                          ]))]))
                        : _createCommentVNode("", true),
                      (
              getSelectedTab(rootTask.id) ===
              _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES
            )
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _cache[12] || (_cache[12] = _createElementVNode("hr", null, null, -1)),
                            _createVNode(ComputeTaskFilesList, {
                              files: rootTask.results.files,
                              onFilePreviewRequested: 
                (file) => filePreviewRequested(rootTask, file)
              ,
                              onFileDownloadRequested: 
                (file) => fileDownloadRequested(rootTask, file)
              ,
                              class: _normalizeClass(
                rootTask.results.files ? ['p-2', 'border', 'rounded'] : []
              )
                            }, null, 8, ["files", "onFilePreviewRequested", "onFileDownloadRequested", "class"])
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                _cache[13] || (_cache[13] = _createElementVNode("hr", null, null, -1)),
                _createElementVNode("div", {
                  class: "tasks-grid",
                  style: _normalizeStyle(`--col-min:${colMinWidth}px`)
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rootTask.subtasks, (subtask, subIdx) => {
                    return (_openBlock(), _createElementBlock(_Fragment, { key: subIdx }, [
                      subtask
                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                            (getExpanded(subtask.id))
                              ? (_openBlock(), _createBlock(TaskResultState, {
                                  key: 0,
                                  error: _unref(detailErrors)[subtask.id!]
                                }, null, 8, ["error"]))
                              : _createCommentVNode("", true),
                            _createVNode(ComputeTaskDetail, {
                              id: `task-${subtask.id}`,
                              task: subtask,
                              expanded: getExpanded(subtask.id),
                              "selected-tab": getSelectedTab(subtask.id),
                              "onUpdate:expanded": val => setExpanded(subtask.id!, val),
                              "onUpdate:selectedTab": val => setSelectedTab(subtask.id!, val),
                              onLayoutNeeded: onLayoutNeeded,
                              onBucketRequested: ($event: any) => (bucketRequested(subtask.bucket, idx, $event)),
                              onExecDetailsRequested: ($event: any) => (showExecDetails(subtask)),
                              onDebugDetailsRequested: ($event: any) => (debugTaskId.value = subtask.id || null),
                              onAbortRequested: ($event: any) => (taskAbortRequested(subtask)),
                              onFilePreviewRequested: 
                  (file) => filePreviewRequested(subtask, file)
                ,
                              onFileDownloadRequested: 
                  (file) => fileDownloadRequested(subtask, file)
                
                            }, null, 8, ["id", "task", "expanded", "selected-tab", "onUpdate:expanded", "onUpdate:selectedTab", "onBucketRequested", "onExecDetailsRequested", "onDebugDetailsRequested", "onAbortRequested", "onFilePreviewRequested", "onFileDownloadRequested"])
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  }), 128))
                ], 4)
              ]),
              _: 2
            }, 1024))
      ], 64))
    }), 128)),
    (__props.pagingItemsPerPage > 0 && pagerTotalRows.value > props.pagingItemsPerPage)
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 2,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagerTotalRows.value,
          "per-page": __props.pagingItemsPerPage,
          "aria-label": "Legacy task pages",
          pills: "",
          size: "sm"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true),
    (debugTaskId.value)
      ? (_openBlock(), _createBlock(ComputeTaskDebugDetails, {
          key: debugTaskId.value,
          "task-id": debugTaskId.value,
          "project-id": props.projectId,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (debugTaskId.value = null))
        }, null, 8, ["task-id", "project-id"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BModal), {
      modelValue: showDetailsModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showDetailsModal).value = $event)),
      class: _normalizeClass([detailsContent.value ? 'modal-xl' : 'modal-sm']),
      onClose: _cache[4] || (_cache[4] = ($event: any) => {
        showDetailsModal.value = false;
        detailsContent.value = null;
      })
    }, {
      header: _withCtx(() => [
        _createElementVNode("h2", null, [
          _createVNode(_component_font_awesome_icon, {
            icon: "sliders",
            class: "me-2"
          }),
          _createTextVNode(" " + _toDisplayString(detailsModalTitle.value), 1)
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: _cache[2] || (_cache[2] = ($event: any) => {
            showDetailsModal.value = false;
            detailsContent.value = null;
          })
        }, {
          default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          (detailsContent.value)
            ? (_openBlock(), _createElementBlock("pre", {
                key: 0,
                class: "code-highlight",
                innerHTML: _unref(highlightJson)(detailsContent.value)
              }, null, 8, _hoisted_12))
            : (_openBlock(), _createElementBlock("p", _hoisted_13, [
                _createVNode(_component_font_awesome_icon, { icon: "exclamation-triangle" }),
                _cache[14] || (_cache[14] = _createElementVNode("strong", null, "No Details", -1))
              ]))
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "class"]),
    (
        selectedDataObject.value &&
        selectedTask.value?.bucket?.name &&
        selectedTask.value?.bucket?.id
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createVNode(DataObjectDetail, {
            show: showModalDataObjectDetail.value,
            "data-object": selectedDataObject.value,
            "bucket-name": selectedTask.value.bucket.name,
            "bucket-id": selectedTask.value.bucket.id,
            "allow-download": true,
            onClose: _cache[5] || (_cache[5] = ($event: any) => {
          showModalDataObjectDetail.value = false;
          selectedDataObject.value = null;
        })
          }, null, 8, ["show", "data-object", "bucket-name", "bucket-id"])
        ]))
      : _createCommentVNode("", true),
    (showConfirmAbortModal.value && selectedTask.value?.id)
      ? (_openBlock(), _createBlock(ComputeTaskAbort, {
          key: 5,
          show: showConfirmAbortModal.value,
          task: selectedTask.value,
          onClose: closeTaskAbortModal,
          onCloseAndRefresh: closeTaskAbortModal
        }, null, 8, ["show", "task"]))
      : _createCommentVNode("", true)
  ]))
}
}

})